/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-06-10 18:10:18
 *
 *******************************************************/
//@flow
import _ from 'lodash';

import { withI18n } from '@shopify/react-i18n';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Banner } from '@shopify/polaris';
import SystemNotifications from './systemNotifications';
import history from 'src/routes/history';
import { routes } from 'src/routes';

import { createMarkup, getFirstCharacter } from 'src/helpers';
import { getShop } from 'src/helpers';

import * as notificationAction from 'actions/dashboard/notification-actions';

type Props = {
  notifications: Array<Object>,
  dismissNotification: Function,
  settings: object
};

type State = {
  dismissList: Array<string>
};
class Notifications extends Component<Props, State> {
  state = {
    dismissList: []
  };

  componentDidMount() {
    // Get dismissList from local storage
    // Check show info banner
    let dismissList = [];
    try {
      dismissList = JSON.parse(
        localStorage.getItem(`${getShop()}-notificationDismissList`) || '[]'
      );
    } catch (error) {}

    this.setState({ dismissList });
  }

  render() {
    const { notifications = [], settings, i18n } = this.props;

    const isBetaLaunch = _.get(settings, 'betaLaunchCampaign.settings.enable', false);

    return (
      <div className="BC-Container__Notifications">
        <SystemNotifications />
        {isBetaLaunch && this.renderNotificationForBetaUsers(i18n)}
        {_.map(notifications, (item, index) => this.renderNotification(item, index))}
      </div>
    );
  }

  renderNotification = (notification, index) => {
    const { dismissList } = this.state;
    const { contents = {}, type = 'information', allowDismiss = false, id = '' } = notification;
    const dismissIndex = _.findIndex(dismissList, item => item === id);

    if (_.isEmpty(contents) || dismissIndex !== -1) {
      return '';
    }

    const { buttonCTALink, buttonLabel, description, title, useButtonCTA } = contents;

    const propAction =
      useButtonCTA && !_.isEmpty(buttonCTALink)
        ? { action: this.buildAction(buttonCTALink, buttonLabel) }
        : {};
    const status = {
      information: 'info',
      warning: 'warning',
      error: 'critical',
      success: 'success'
    };

    return (
      <div className="notification" key={index}>
        <Banner
          title={title}
          status={status[type]}
          {...propAction}
          onDismiss={
            allowDismiss || allowDismiss === undefined ? this.onDismiss.bind(this, id) : ''
          }>
          <p dangerouslySetInnerHTML={createMarkup(description)}></p>
        </Banner>
      </div>
    );
  };

  renderNotificationForBetaUsers = i18n => {
    return (
      <div className="notification" key="beta-user">
        <Banner
          title={i18n.translate('AdminHTML.Dashboard.NotificationBlockForBetaUser.title')}
          status="info"
          onDismiss={true}>
          <p>
            <p style={{ marginBottom: '20px' }}>
              {i18n.translate('AdminHTML.Dashboard.NotificationBlockForBetaUser.subtext01')}
            </p>
            <p style={{ marginBottom: '10px' }}>
              {i18n.translate('AdminHTML.Dashboard.NotificationBlockForBetaUser.content01', {
                t1: (
                  <strong
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() =>
                      history.push(
                        `${routes.merchandising.view()}?&itm_source=inapp&itm_medium=notification&itm_campaign=BetaLaunch23`
                      )
                    }>
                    {i18n.translate('AdminHTML.Dashboard.NotificationBlockForBetaUser.label01')}
                  </strong>
                )
              })}
            </p>
            <p style={{ marginBottom: '10px' }}>
              {i18n.translate('AdminHTML.Dashboard.NotificationBlockForBetaUser.content02', {
                t1: (
                  <strong
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() =>
                      history.push(
                        `${routes.synonym.view()}?&itm_source=inapp&itm_medium=notification&itm_campaign=BetaLaunch23`
                      )
                    }>
                    {i18n.translate('AdminHTML.Dashboard.NotificationBlockForBetaUser.label02')}
                  </strong>
                )
              })}
            </p>
            <p style={{ marginBottom: '10px' }}>
              {i18n.translate('AdminHTML.Dashboard.NotificationBlockForBetaUser.content03', {
                t1: (
                  <strong
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() =>
                      history.push(
                        `${routes.recommendationWidgets.list()}?&itm_source=inapp&itm_medium=notification&itm_campaign=BetaLaunch23`
                      )
                    }>
                    {i18n.translate('AdminHTML.Dashboard.NotificationBlockForBetaUser.label03')}
                  </strong>
                )
              })}
            </p>

            <strong style={{ fontStyle: 'italic' }}>
              {i18n.translate('AdminHTML.Dashboard.NotificationBlockForBetaUser.subtext02')}
            </strong>
          </p>
        </Banner>
      </div>
    );
  };

  buildAction(action, actionLabel) {
    if (_.isEmpty(action)) return {};

    if (getFirstCharacter(action) === '/') {
      return {
        onAction: () => {
          history.push(action);
        },
        content: actionLabel
      };
    }

    return {
      url: action,
      content: actionLabel,
      external: true,
      onAction: () => {}
    };
  }

  onDismiss = (notificationId: string) => {
    const { dismissList } = this.state;
    dismissList.push(notificationId);
    try {
      localStorage.setItem(`${getShop()}-notificationDismissList`, JSON.stringify(dismissList));
    } catch (error) {}
    this.setState({ dismissList });
  };
}

const mapStateToProps = (state, props) => {
  const notifications = _.get(state.dashboard, 'dashboardComponents.notifications.data', []);
  const settings = _.get(state.oauth, 'tenantApp.settings', {});

  return { notifications, settings };
};

const mapDispatchToProps = dispatch => ({
  dismissNotification: notificationId =>
    dispatch(notificationAction.dismissNotification(notificationId))
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(Notifications));
